import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import {
  apiClientTokenEndpoint,
  apiClientTokenSingleEndpoint,
  apiClientTokenToggleEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APIApiClientToken,
  APIApiClientTokenCreateModel,
  APIApiClientTokenSearchFilter,
  APIApiClientTokenSearchResult,
  APIApiClientTokenToggleModel,
  APISortOrder,
} from 'types/api';

export type ApiClientTokenState = {
  tokenList: APIApiClientToken[];
  loadTokensPending: boolean;
  tokensListLoaded: boolean;
  allTokensLoaded: boolean;
  createdToken: APIApiClientToken | null;
  createTokenPending: boolean;
  filters: APIApiClientTokenSearchFilter;
  pagination: {
    pageNumber: APIApiClientTokenSearchResult['pageNumber'];
    pageSize: APIApiClientTokenSearchResult['pageSize'];
  };
};

export const apiClientTokenState: ApiClientTokenState = {
  tokenList: [],
  loadTokensPending: false,
  tokensListLoaded: false,
  allTokensLoaded: false,
  createdToken: null,
  createTokenPending: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'createdOn',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
};

const builder = simplifyBuilder(apiClientTokenState, {});

export const loadTokens = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadTokens',
    url: apiClientTokenEndpoint(organizationId),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      ...state.apiClientToken.filters,
    }),
    method: httpMethod.post,
    onSuccess: (
      state: ApiClientTokenState,
      payload: APIApiClientTokenSearchResult,
    ) => ({
      tokenList: payload.result,
      tokensListLoaded: true,
      allTokensLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMoreTokens = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadMoreTokens',
    url: apiClientTokenEndpoint(organizationId),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.apiClientToken.tokenList,
        state.apiClientToken.pagination.pageSize,
      ),
      ...state.apiClientToken.filters,
    }),
    method: httpMethod.post,
    onSuccess: (
      state: ApiClientTokenState,
      payload: APIApiClientTokenSearchResult,
    ) => ({
      tokenList: [...(state.tokenList || []), ...payload.result],
      tokensListLoaded: true,
      allTokensLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const createToken = builder.createServerAction(
  (organizationId: string, createModel: APIApiClientTokenCreateModel) => ({
    name: 'createToken',
    url: apiClientTokenEndpoint(organizationId),
    body: () => createModel,
    method: httpMethod.put,
    onSuccess: (state: ApiClientTokenState, payload: APIApiClientToken) => ({
      createdToken: payload,
      tokenList: [payload, ...(state.tokenList || [])],
    }),
  }),
);

export const toggleToken = builder.createServerAction(
  (
    organizationId: string,
    tokenId: string,
    toggleModel: APIApiClientTokenToggleModel,
  ) => ({
    name: 'toggleToken',
    url: apiClientTokenToggleEndpoint(organizationId, tokenId),
    body: () => toggleModel,
    method: httpMethod.post,
    onSuccess: (state: ApiClientTokenState, payload: APIApiClientToken) => ({
      tokenList: state.tokenList.map(t => (t.id === payload.id ? payload : t)),
    }),
  }),
);

export const deleteToken = builder.createServerAction(
  (organizationId: string, tokenId: string) => ({
    name: 'deleteToken',
    url: apiClientTokenSingleEndpoint(organizationId, tokenId),
    method: httpMethod.delete,
    onSuccess: (state: ApiClientTokenState) => ({
      tokenList: state.tokenList.filter(t => t.id !== tokenId),
    }),
  }),
);

const updateTokenFilters = builder.createReduxAction(
  (filters: APIApiClientTokenSearchFilter) => ({
    name: 'updateTokenFilters',
    updater: () => ({ filters }),
  }),
);

export const clearTokenList = builder.createReduxAction(() => ({
  name: 'clearTokenList',
  updater: () => ({
    tokenList: [],
    tokensListLoaded: false,
    createdToken: null,
  }),
}));

export const clearCreatedToken = builder.createReduxAction(() => ({
  name: 'clearCreatedToken',
  updater: () => ({ createdToken: null }),
}));

export const useApiClientToken = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updateTokenFilters,
          clearTokenList,
          clearCreatedToken,
          loadTokens: loadTokens.bind(null, organizationId),
          loadMoreTokens: loadMoreTokens.bind(null, organizationId),
          createToken: (model: APIApiClientTokenCreateModel) =>
            createToken(organizationId, model),
          toggleToken: (tokenId: string, model: APIApiClientTokenToggleModel) =>
            toggleToken(organizationId, tokenId, model),
          deleteToken: (tokenId: string) =>
            deleteToken(organizationId, tokenId),
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const apiClientTokenReducer = builder.getReducers();
